import { DestroyRef, Injectable, Signal, computed, inject, signal, DOCUMENT } from '@angular/core';
import { HD_BODYGRAPH_GATES_BY_CENTER, HD_BODYGRAPH_GATE_TO_CENTER } from './hd-bodygraph-metadata';
import { HdBodygraphCenter, HdBodygraphSide } from './hd-bodygraph.models';
import { HdAnnotationStore } from './annotation/hd-annotation-store';

export type HdChartHoverSource =
  | 'chart-gate'
  | 'chart-center'
  | 'planet-grid-block'
  | 'planet-grid-furnishing'
  | 'wheel-gate'
  | 'wheel-line'
  | 'just-now-transit';

/**
 * De twee "kanten" van de hover-highlight voor de setting "enkel één kant
 * dimmen": de chart (bodygraph, mandala-wiel, poortblokjes op de chart) en de
 * planeetgrid (incl. kruizen, furnishings en stats).
 */
export type HdChartHoverSide = 'chart' | 'grid';

/**
 * Kant waar een hover-bron toe behoort. `null` = een bron buiten beide kanten
 * (Just Now Transit-kaarten): die dimt, net als zonder de setting, beide kanten.
 */
const HD_CHART_HOVER_SOURCE_SIDE: Readonly<Record<HdChartHoverSource, HdChartHoverSide | null>> = {
  'chart-gate': 'chart',
  'chart-center': 'chart',
  'wheel-gate': 'chart',
  'wheel-line': 'chart',
  'planet-grid-block': 'grid',
  'planet-grid-furnishing': 'grid',
  'just-now-transit': null,
};

export type HdGateKeynoteHoverSource = 'chart-gate' | 'planet-grid-block';
export type HdKeynoteHoverSource = HdGateKeynoteHoverSource | 'chart-center';
export type HdGateKeynoteHoverKind = 'gate' | 'channel';
export type HdKeynoteHoverKind = HdGateKeynoteHoverKind | 'center';

export type HdKeynoteHoverState =
  | {
    readonly gate: number;
    readonly gates?: never;
    readonly center?: never;
    readonly source: HdGateKeynoteHoverSource;
    readonly kind: 'gate';
  }
  | {
    readonly gate: number;
    readonly gates: Signal<ReadonlySet<number>>;
    readonly center?: never;
    readonly source: HdGateKeynoteHoverSource;
    readonly kind: 'channel';
  }
  | {
    readonly gate?: never;
    readonly gates?: never;
    readonly center: HdBodygraphCenter;
    readonly source: 'chart-center';
    readonly kind: 'center';
  };

export type HdChartHoverState =
  | { readonly kind: 'gate'; readonly gate: number; readonly source: HdChartHoverSource }
  | { readonly kind: 'gates'; readonly gates: ReadonlySet<number>; readonly source: HdChartHoverSource }
  | { readonly kind: 'center'; readonly center: HdBodygraphCenter; readonly source: 'chart-center' };

/**
 * Eén bevroren entry. Heeft géén `source` (irrelevant na bevriezing) en is
 * structureel identiek aan een {@link HdChartHoverState} zonder source-veld.
 * Zie `docs/human-design/hd-chart-hover-freeze-additief.md` §3.
 */
export type HdChartHoverItem =
  | { readonly kind: 'gate'; readonly gate: number }
  | { readonly kind: 'gates'; readonly gates: ReadonlySet<number> }
  | { readonly kind: 'center'; readonly center: HdBodygraphCenter };

export type HdChartHoverTarget =
  | { readonly kind: 'gate'; readonly gate: number }
  | { readonly kind: 'center'; readonly center: HdBodygraphCenter };

/**
 * Side-channel describing the LPS planet-block that should render an extra
 * "echo" of a source base-badge while the user hovers that source base-badge.
 * Resolved by the source `HdPlanetActivationBlockComponent` from the
 * activation's base + planet key + side; consumed by every activation block
 * (each one checks if its own `side` + `planetId` matches).
 *
 *  - `side` / `planetId`: identify the unique target LPS block.
 *  - `badgeState`: the source's already-rotated quadrant state, rendered
 *    as-is on the target so the user sees the exact same shape they hover.
 *  - `base`: the source's base number, rendered in the echo badge.
 *  - `quadrantColor`: the source's resolved quadrant color, rendered as-is on
 *    the echo so it matches the source badge (red in classic rotation, the
 *    LPS blue in LPS rotation).
 *
 * See `.\docs\human-design\base-logic.md` (§ Base Hover → LPS Highlight).
 */
export interface HdBaseHoverTarget {
  readonly side: HdBodygraphSide;
  readonly planetId: number;
  readonly badgeState: {
    readonly tl: boolean;
    readonly tr: boolean;
    readonly bl: boolean;
    readonly br: boolean;
  };
  readonly base: number;
  readonly quadrantColor: string;
}

/**
 * Centrale state voor het hover-highlight gedrag van de chart + planet-grid.
 * Eén instantie wordt voorzien op de `chart-editor-page` zodat alle relevante
 * componenten (bodygraph-chart, planet-grid, activation-blocks) er optioneel
 * op kunnen inhaken. Componenten die buiten de editor-page gebruikt worden
 * (bv. cheat-sheet pages) injecteren optioneel en zijn no-op.
 *
 * Zie `docs/human-design/hd-chart-hover-highlight.md` voor het basismechanisme
 * en `docs/human-design/hd-chart-hover-freeze-additief.md` voor de additieve
 * freeze-flow (meerdere items kunnen tegelijk bevroren staan; live hover blijft
 * bovenop werken en wordt door normale mouse-events beheerd).
 */
/**
 * Korte vertraging vooraleer een hover-state effectief gepubliceerd wordt.
 * Hierdoor activeert het highlight-effect niet bij snel over de chart of
 * planet-grid bewegen — enkel wanneer de cursor effectief stilstaat op een
 * element wordt de state gezet en de bijhorende DOM-bewerkingen uitgevoerd.
 */
const HD_HOVER_ENTER_DELAY_MS = 72;

/**
 * Korte vertraging vooraleer een hover-clear effectief gepubliceerd wordt.
 * Reden: bij een typische A→B beweging (cursor verlaat gate A en bereikt
 * binnen enkele ms gate B) wil je geen tussenliggende `state=null` zien.
 * Een instant clear veroorzaakt anders:
 *  - twee effect-cycli i.p.v. één (clear + set);
 *  - een full-pass in `applyHoverDimDelta` op de nieuwe enter (omdat de clear
 *    `currentHoverKeepUnion` op `null` zet, waardoor de XOR-delta z'n vorige
 *    keep-union verliest);
 *  - een visuele "undim + redim" flash tijdens de korte gap.
 * Door de clear te debouncen wordt een binnen-72ms volgende enter de clear
 * canceled, zodat de transitie tussen targets als één enkele delta-pass
 * landt — zonder flash en met merkbaar minder DOM-werk.
 */
const HD_HOVER_LEAVE_DELAY_MS = 72;

@Injectable()
export class HdChartHoverHighlightService {
  private readonly _enabled = signal(false);
  /**
   * App-setting "dimmen bij hover" (default aan). Wanneer `false` tonen alle
   * consumers enkel de blauwe-rand-highlights: niets wordt gedimd, en élk
   * matchend element (ook extra activaties van dezelfde poort in grid/mandala)
   * krijgt een blauwe rand. Geldt identiek voor bevroren items. Wordt door de
   * chart-pagina gewired vanuit de {@link HdChartSettingsStore} (zelfde patroon
   * als {@link keynotesAtHoverOnly}).
   */
  private readonly _dimOnHover = signal(true);
  /**
   * App-setting "enkel één kant dimmen" (default uit). Aan: enkel de kant
   * tegenover de hover-bron dimt (zie {@link dimsChart} / {@link dimsGrid});
   * de highlights zelf (source-randen, matches) blijven ongewijzigd.
   */
  private readonly _dimOneSideOnly = signal(false);
  /**
   * App-setting "poortstukken highlighten" (default uit): de chart tekent een
   * outline rond het kanaalstuk van elke gehighlighte poort.
   */
  private readonly _highlightGatePieces = signal(false);
  /**
   * Kant van de laatst gepubliceerde live hover; een stat-freeze (klik op een
   * stat-rij) telt als grid, een freeze vanuit de configurator zonder live
   * hover als `null` (beide kanten dimmen).
   *
   * BELANGRIJK — bewust "sticky": de kant blijft staan wanneer de live hover
   * wegvalt. Zo volgt een freeze de kant waar hij gemaakt werd, én flitst het
   * mandala-wiel niet bij het verlaten: het wiel debouncet zijn lokale hover
   * met een eigen timer die net ná de service-clear vuurt. Een reset naar
   * `null` op dat moment liet het wiel één tick dimmen (null = beide kanten
   * dimmen). Enkel {@link clear} en `setEnabled(false)` wissen hem. Gedekt door
   * de spec "dim one side only".
   */
  private readonly _originSide = signal<HdChartHoverSide | null>(null);
  private readonly _state = signal<HdChartHoverState | null>(null);
  private readonly _keynoteHover = signal<HdKeynoteHoverState | null>(null);
  private readonly _keynotesAtHoverOnly = signal(true);
  /**
   * De bevroren set. Onafhankelijk van de live `_state` en wordt enkel door
   * expliciete gebruikersacties (contextmenu, Ctrl+klik, Esc, unfreeze-button,
   * setEnabled(false)) gemuteerd. `frozen()` is een computed afgeleide van
   * `_frozenItems().length > 0` — geen aparte signal om desync te voorkomen.
   */
  private readonly _frozenItems = signal<readonly HdChartHoverItem[]>([]);
  /**
   * De stat-rij (lijn / slapend / gedefinieerd / circuit) die momenteel bevroren
   * is, samen met de exacte item-referentie die daarvoor in `_frozenItems` staat.
   * Nodig omdat "welke rij" niet uit de gate-set af te leiden is: gate-sets van
   * verschillende rijen overlappen, en een rij kan toevallig volledig bevroren
   * zijn door een actie elders (chart, configurator). De item-referentie maakt
   * verwijderen exact — een identieke set die elders bevroren werd blijft staan.
   */
  private readonly _frozenStat = signal<{
    readonly key: string;
    readonly item: HdChartHoverItem;
  } | null>(null);
  /**
   * Side-channel voor de "echo" base-badge die op het LPS-target getoond
   * wordt zolang de gebruiker over een source base-badge hovert. Volledig
   * onafhankelijk van `_state` / `_frozenItems`: enkel een visuele extra,
   * geen invloed op gate/center matching. Wordt direct gezet (zonder
   * enter/leave debounce) zodat het echo-badge synchroon verschijnt met
   * de gates-hover die door dezelfde mouseenter wordt gepubliceerd.
   */
  private readonly _baseHoverTarget = signal<HdBaseHoverTarget | null>(null);
  /**
   * Tijdens een actieve rechterklik-contextmenu pauzeert de service alle
   * hover-state mutaties. Reden: het CDK-overlay van het menu komt boven
   * de cursor te liggen waardoor de browser een `mouseleave` op het
   * onderliggende gate/center/planet-block element fired. Zonder pauze
   * zou dat de state clearen vóór de gebruiker "Bevriezen" kan kiezen.
   * Wordt gezet door {@link suspendForContextMenu} en opgeheven door
   * {@link resumeFromContextMenu} (typisch gewired aan de menu-close-callback).
   */
  private readonly _suspended = signal(false);
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingKeynoteTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingKeynoteSource: HdKeynoteHoverSource | null = null;

  /**
   * Synchrone luisteraars die direct na **elke** mutatie van de chart-
   * relevante state (`_state`, `_frozenItems`, `_enabled`) worden aangeroepen.
   * Bedoeld voor de bodygraph-chart, die zijn SVG-dimming via directe
   * classList-mutaties toepast: door op deze listener te draaien i.p.v. een
   * `effect()` (microtask) landt de chart-dimming in **dezelfde call-stack**
   * als de signal-write die — binnen de Angular zone — de planet-grid en het
   * mandala-wiel via change-detection updaten. Zo starten alle drie de
   * visuele transities in dezelfde frame i.p.v. de chart één frame later
   * (de "2-staps dim"-regressie). Zie
   * `docs/human-design/hd-chart-hover-2-step-regressie-analyse.md` §7.
   */
  private readonly stateChangeListeners = new Set<() => void>();

  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly annotationStore = inject(HdAnnotationStore, { optional: true });

  constructor() {
    this.installDocumentMouseLeaveSafetyNet();
    this.installEscUnfreezeListener();
  }

  readonly enabled = this._enabled.asReadonly();
  readonly dimOnHover = this._dimOnHover.asReadonly();
  readonly dimOneSideOnly = this._dimOneSideOnly.asReadonly();
  readonly highlightGatePieces = this._highlightGatePieces.asReadonly();
  readonly originSide = this._originSide.asReadonly();
  readonly state = this._state.asReadonly();

  /**
   * Of de chart-kant (bodygraph, mandala, poortblokjes) bij een actieve
   * hover/freeze dimt. Los van {@link dimOnHover}: wanneer dimmen globaal uit
   * staat tonen consumers hun match-randen; hier enkel "dimt deze kant".
   */
  readonly dimsChart = computed(() => this.dimsSide('chart'));

  /** Idem {@link dimsChart} voor de planeetgrid-kant (blokken, kruizen, stats). */
  readonly dimsGrid = computed(() => this.dimsSide('grid'));
  readonly keynoteHover = this._keynoteHover.asReadonly();
  readonly keynotesAtHoverOnly = this._keynotesAtHoverOnly.asReadonly();
  readonly frozenItems = this._frozenItems.asReadonly();
  readonly baseHoverTarget = this._baseHoverTarget.asReadonly();
  /**
   * Afgeleide vlag: er is minstens één bevroren item. Wordt door de controls-bar
   * gebruikt voor de freeze-cue en door het document-mouseleave-safetynet
   * (§6.7). Geen aparte signal — één source of truth via `_frozenItems`.
   */
  readonly frozen = computed(() => this._frozenItems().length > 0);

  /**
   * Key van de stat-rij die momenteel bevroren is, of `null`. Consumers
   * (line-/circuit-stats, planet-grid) vergelijken hun eigen rij-key hiermee.
   * Valt automatisch terug op `null` zodra het bijhorende item uit de bevroren
   * set verdwijnt — zie {@link writeFrozenItems}.
   */
  readonly frozenStatKey = computed<string | null>(() => this._frozenStat()?.key ?? null);

  readonly hoveredGate = computed<number | null>(() => {
    const s = this._state();
    return s?.kind === 'gate' ? s.gate : null;
  });

  readonly hoveredCenter = computed<HdBodygraphCenter | null>(() => {
    const s = this._state();
    return s?.kind === 'center' ? s.center : null;
  });

  /**
   * Effectieve set van "actieve" gates: union van de live state en alle
   * gate/gates-items uit de bevroren set. Center-items zitten hier NIET in —
   * die worden via {@link effectiveCenters} apart blootgesteld zodat
   * consumers de twee match-paden (gate-match vs center-match) onafhankelijk
   * kunnen behandelen.
   */
  readonly effectiveGates = computed<ReadonlySet<number>>(() => {
    const result = new Set<number>();
    for (const item of this._frozenItems()) {
      if (item.kind === 'gate') result.add(item.gate);
      else if (item.kind === 'gates') item.gates.forEach(g => result.add(g));
    }
    const s = this._state();
    if (s != null) {
      if (s.kind === 'gate') result.add(s.gate);
      else if (s.kind === 'gates') s.gates.forEach(g => result.add(g));
    }
    return result;
  });

  readonly effectiveCenters = computed<ReadonlySet<HdBodygraphCenter>>(() => {
    const result = new Set<HdBodygraphCenter>();
    for (const item of this._frozenItems()) {
      if (item.kind === 'center') result.add(item.center);
    }
    const s = this._state();
    if (s?.kind === 'center') result.add(s.center);
    return result;
  });

  readonly isActive = computed(() =>
    this._enabled() && (this._state() != null || this._frozenItems().length > 0),
  );

  /**
   * Bepaalt of een gegeven poort "matcht" met de effectieve hover/freeze-state.
   *
   * - Match wanneer de poort in {@link effectiveGates} zit (live-gate-hover of
   *   bevroren gate/gates-item).
   * - Match wanneer de poort tot een center behoort dat in
   *   {@link effectiveCenters} zit (center-hover of bevroren center-item).
   *   Hierdoor lichten planet-grid-blokjes ook mee op bij een center-hover
   *   in de chart, en blijven ze opgelicht zolang het center bevroren is.
   * - Wanneer de service uitgeschakeld is: altijd `false`.
   */
  isGateMatchingHover(gate: number): boolean {
    if (!this._enabled()) return false;
    if (this.effectiveGates().has(gate)) return true;
    const center = HD_BODYGRAPH_GATE_TO_CENTER[gate];
    if (center != null && this.effectiveCenters().has(center)) return true;
    return false;
  }

  /**
   * Registreert een synchrone luisteraar die na elke chart-relevante
   * state-mutatie (zie {@link stateChangeListeners}) wordt aangeroepen. De
   * callback leest zelf de huidige {@link enabled}/{@link effectiveGates}/
   * {@link effectiveCenters} — hij ontvangt geen argument. Retourneert een
   * unsubscribe-functie.
   */
  onStateChange(listener: () => void): () => void {
    this.stateChangeListeners.add(listener);
    return () => {
      this.stateChangeListeners.delete(listener);
    };
  }

  /**
   * Zet de "dimmen bij hover"-voorkeur (zie {@link dimOnHover}). Loopt via
   * {@link notifyStateChange} zodat de bodygraph-chart (synchrone listener)
   * een al actieve hover/freeze meteen her-rendert wanneer de setting
   * toggelt — signal-consumers (grid, mandala) volgen via hun eigen bindings.
   */
  setDimOnHover(value: boolean): void {
    if (this._dimOnHover() === value) return;
    this._dimOnHover.set(value);
    this.notifyStateChange();
  }

  /** Zet "enkel één kant dimmen"; zelfde her-render-pad als {@link setDimOnHover}. */
  setDimOneSideOnly(value: boolean): void {
    if (this._dimOneSideOnly() === value) return;
    this._dimOneSideOnly.set(value);
    this.notifyStateChange();
  }

  /** Zet "poortstukken highlighten"; zelfde her-render-pad als {@link setDimOnHover}. */
  setHighlightGatePieces(value: boolean): void {
    if (this._highlightGatePieces() === value) return;
    this._highlightGatePieces.set(value);
    this.notifyStateChange();
  }

  setEnabled(enabled: boolean): void {
    if (this._enabled() === enabled) return;
    this.writeEnabled(enabled);
    if (!enabled) {
      this.cancelPending();
      this._originSide.set(null);
      this.writeState(null);
      this.writeFrozenItems([]);
      this._baseHoverTarget.set(null);
      this._suspended.set(false);
    }
  }

  /**
   * Publiceert (of wist) het "echo" base-badge target — de LPS-blok dat een
   * extra base-indicator toont zolang de gebruiker over een source
   * base-badge hovert. Geen debounce: visueel hoort het 1-op-1 bij de
   * mouseenter/mouseleave van het source-badge.
   */
  setBaseHoverTarget(target: HdBaseHoverTarget | null): void {
    if (!this._enabled() && target != null) return;
    if (this._suspended()) return;
    this._baseHoverTarget.set(target);
  }

  setGateHover(gate: number | null, source: HdChartHoverSource): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    this.cancelPending();
    if (gate == null) {
      this.scheduleClear(source);
      return;
    }
    this.scheduleSet({ kind: 'gate', gate, source });
  }

  setKeynoteHover(
    gate: number,
    source: HdGateKeynoteHoverSource,
    kind?: 'gate',
  ): void;
  setKeynoteHover(
    gate: number,
    source: HdGateKeynoteHoverSource,
    kind: 'channel',
    channelGates: Signal<ReadonlySet<number>>,
  ): void;
  setKeynoteHover(gate: null, source: HdGateKeynoteHoverSource): void;
  setKeynoteHover(
    gate: number | null,
    source: HdGateKeynoteHoverSource,
    kind: HdGateKeynoteHoverKind = 'gate',
    channelGates?: Signal<ReadonlySet<number>>,
  ): void {
    if (gate != null) {
      let nextState: HdKeynoteHoverState;
      if (kind === 'channel') {
        if (channelGates == null) return;
        nextState = { gate, gates: channelGates, source, kind };
      } else {
        nextState = { gate, source, kind };
      }
      this.scheduleKeynoteSet(nextState);
      return;
    }

    this.scheduleKeynoteClear(source);
  }

  setCenterKeynoteHover(center: HdBodygraphCenter | null): void {
    if (center != null) {
      this.scheduleKeynoteSet({ center, source: 'chart-center', kind: 'center' });
      return;
    }

    this.scheduleKeynoteClear('chart-center');
  }

  private scheduleKeynoteSet(nextState: HdKeynoteHoverState): void {
    this.cancelPendingKeynote();
    this.pendingKeynoteSource = nextState.source;
    this.pendingKeynoteTimer = setTimeout(() => {
      this.pendingKeynoteTimer = null;
      this.pendingKeynoteSource = null;
      this._keynoteHover.set(nextState);
    }, HD_HOVER_ENTER_DELAY_MS);
  }

  private scheduleKeynoteClear(source: HdKeynoteHoverSource): void {
    if (this.pendingKeynoteSource != null && this.pendingKeynoteSource !== source) {
      return;
    }

    this.cancelPendingKeynote();
    if (this._keynoteHover()?.source !== source) return;

    this.pendingKeynoteSource = source;
    this.pendingKeynoteTimer = setTimeout(() => {
      this.pendingKeynoteTimer = null;
      this.pendingKeynoteSource = null;
      if (this._keynoteHover()?.source === source) {
        this._keynoteHover.set(null);
      }
    }, HD_HOVER_LEAVE_DELAY_MS);
  }

  setKeynotesAtHoverOnly(value: boolean): void {
    this._keynotesAtHoverOnly.set(value);
  }

  /**
   * Variant op {@link setGateHover} voor "furnishings" (kleur/toon-blokken en
   * transformatiepijlen) die conceptueel bij méérdere poorten horen
   * (motivatie = Sun + Earth, view = NN + SN). Lichten dezelfde poorten op
   * de chart op als wanneer die afzonderlijk gehoverd zouden worden.
   */
  setGatesHover(gates: readonly number[] | null, source: HdChartHoverSource): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    this.cancelPending();
    if (gates == null || gates.length === 0) {
      this.scheduleClear(source);
      return;
    }
    this.scheduleSet({ kind: 'gates', gates: new Set(gates), source });
  }

  setCenterHover(center: HdBodygraphCenter | null): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    this.cancelPending();
    if (center == null) {
      this.scheduleClear('chart-center');
      return;
    }
    this.scheduleSet({ kind: 'center', center, source: 'chart-center' });
  }

  clear(): void {
    this.cancelPending();
    this.cancelPendingKeynote();
    this._originSide.set(null);
    this.writeState(null);
    this.writeFrozenItems([]);
    this._keynoteHover.set(null);
    this._baseHoverTarget.set(null);
    this._suspended.set(false);
  }

  /**
   * Pauzeert alle hover-state mutaties tot {@link resumeFromContextMenu}.
   * Bedoeld om aangeroepen te worden in de `contextmenu`-event-handler van
   * hover-targets (chart-gate, chart-center, planet-grid-block) **vóór** het
   * emit van de open-aanvraag, zodat het CDK-overlay van het menu de
   * onderliggende hover-state niet kan wegtrekken. Cancelt eventueel
   * pending enter/leave-timers maar laat de zichtbare state intact zodat
   * een eventuele "Bevriezen" daadwerkelijk iets te bevriezen heeft.
   *
   * Idempotent: opeenvolgende suspends op dezelfde menu-cyclus zijn no-op.
   */
  suspendForContextMenu(): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    this.cancelPending();
    this._suspended.set(true);
  }

  /**
   * Heft een eerdere {@link suspendForContextMenu} op. Wanneer er nog een
   * hover-state actief is plannen we een gewone leave-clear (72ms) zodat de
   * cursor-positie weer driving wordt: blijft de cursor op het oorspronkelijke
   * target, dan triggert die opnieuw een `mouseenter`. Frozen items blijven
   * sowieso staan — die zijn onafhankelijk van het cursor-pad.
   */
  resumeFromContextMenu(): void {
    if (!this._suspended()) return;
    this._suspended.set(false);
    const current = this._state();
    if (current == null) return;
    this.scheduleClear(current.source);
  }

  /**
   * "Freeze snapshot": voegt de huidige live `_state` toe als nieuw item aan
   * de bevroren set. Bedoeld voor screenshots/print of om in alle rust naar
   * een specifieke highlight te kijken, of om iteratief meerdere items te
   * combineren. No-op wanneer er geen hover-state staat (niets om te
   * bevriezen) of wanneer de service uitgeschakeld is. Dedupe binnen
   * hetzelfde kind (zie §6.2 / §6.4): identieke gates en hetzelfde center
   * worden overgeslagen, cross-kind blokkeert nooit.
   *
   * Behavior wijziging t.o.v. de vorige implementatie: hover-setters worden
   * NIET meer no-op zodra er bevroren items zijn; volgende hovers blijven
   * `_state` muteren. Page-level imperatieve calls (`clear`,
   * `setEnabled(false)`, "Ontdooien Alles") heffen de freeze wel volledig op.
   */
  freezeSnapshot(): void {
    this.addCurrentHoverToFreeze();
  }

  /**
   * Backing voor "Bevriezen / Freeze" — voegt de huidige live-hover toe aan
   * de bevroren set. Dedupe binnen hetzelfde kind (§6.2). Cross-kind nooit
   * blokkeren (§6.4).
   */
  addCurrentHoverToFreeze(): void {
    if (!this._enabled()) return;
    const live = this._state();
    if (live == null) return;
    this.cancelPending();
    const items = this._frozenItems();
    const nextItem = this.buildFrozenItem(live, items);
    if (nextItem == null) return;
    this.writeFrozenItems([...items, nextItem]);
  }

  /**
   * Voegt een groep gates ineens toe aan de bevroren set als één `gates`-item.
   * Bedoeld voor "klik op circuit in freeze-modus": meerdere poorten worden
   * in één render-cyclus als één logisch item toegevoegd (één signal-mutatie
   * = één `effect()`-pass).
   *
   * Dedupe-regel: als er al een `gates`-item met exact dezelfde gate-set in
   * `_frozenItems` zit, wordt geen tweede push uitgevoerd. Cross-kind
   * tolerantie blijft: bestaande `gate`-/`center`-items raken we niet aan.
   * No-op bij lege input of uitgeschakelde service.
   */
  addGatesToFreeze(gates: readonly number[]): void {
    if (!this.enabled()) return;
    if (gates.length === 0) return;
    const incoming = new Set<number>(gates);
    if (incoming.size === 0) return;
    const items = this._frozenItems();
    for (const item of items) {
      if (item.kind !== 'gates') continue;
      if (item.gates.size !== incoming.size) continue;
      let same = true;
      for (const g of incoming) {
        if (!item.gates.has(g)) { same = false; break; }
      }
      if (same) return;
    }
    // Zonder live hover komt de freeze van buiten chart en grid (configurator):
    // geen herkomstkant, dus dimmen beide kanten. Met live hover (Ctrl+klik op
    // een kanaalstuk) blijft de kant van die hover gelden.
    if (this._state() == null) this._originSide.set(null);
    this.writeFrozenItems([...items, { kind: 'gates', gates: incoming }]);
  }

  /**
   * Toggle-variant van {@link toggleFreezeForTarget} voor een poorten-SET —
   * gebruikt door Ctrl/Cmd+klik op een kanaalstuk met kanaal-highlight
   * (spec kanaalhighlight §3.6): het hele kanaal gedraagt zich als één
   * geheel. Alle poorten al bevroren (als gate-item of via gates-items) →
   * allemaal verwijderen; anders de set als één `gates`-item toevoegen
   * (dedupe via {@link addGatesToFreeze}).
   */
  toggleFreezeForGates(gates: readonly number[]): void {
    if (!this._enabled()) return;
    if (gates.length === 0) return;
    const allFrozen = gates.every(gate => this.isTargetFullyFrozen({ kind: 'gate', gate }));
    if (allFrozen) {
      for (const gate of gates) {
        this.removeFromFreeze({ kind: 'gate', gate });
      }
    } else {
      this.addGatesToFreeze(gates);
    }
  }

  /**
   * Toggle voor een klikbare stat-rij: bevriest de poorten achter die
   * statistiek, of ontdooit ze wanneer dezelfde rij opnieuw wordt aangeklikt.
   *
   * Additief t.o.v. andere freezes (chart-gate, circuit-pil), maar maximaal
   * één stat-rij tegelijk: een klik op een andere rij vervangt de vorige.
   * Alles landt in één {@link writeFrozenItems}-call zodat er één listener-pass
   * gebeurt.
   *
   * Bewust géén hergebruik van {@link addGatesToFreeze}: die dedupe't op een
   * identieke gate-set en zou ons dan geen item-referentie opleveren om later
   * gericht te verwijderen. Gevolg is dat een exact identieke set die elders al
   * bevroren staat hier een duplicaat-item oplevert — onschadelijk, want
   * {@link effectiveGates} is een union.
   */
  toggleStatFreeze(key: string, gates: readonly number[]): void {
    if (!this._enabled()) return;
    if (gates.length === 0) return;

    const previous = this._frozenStat();
    const remaining = previous == null
      ? this._frozenItems()
      : this._frozenItems().filter(item => item !== previous.item);

    if (previous?.key === key) {
      this._frozenStat.set(null);
      this.writeFrozenItems(remaining);
      return;
    }

    const item: HdChartHoverItem = { kind: 'gates', gates: new Set<number>(gates) };
    this._frozenStat.set({ key, item });
    // De stat-rijen staan in de planeetgrid: voor "enkel één kant dimmen" is
    // dit dus een grid-interactie (de chart dimt, de grid niet).
    this._originSide.set('grid');
    this.writeFrozenItems([...remaining, item]);
  }

  /**
   * Heft de volledige freeze op: wist alle bevroren items en de live state.
   * Reden voor direct wissen van de live state: de cursor zit op het
   * unfreeze-moment doorgaans niet meer boven het oorspronkelijke
   * hover-target (klik op controls-bar-icoon, Esc, of menu-item "Ontdooien
   * Alles" vanaf willekeurige plek). Een nieuwe hover-enter zet de state
   * binnen de gewone `HD_HOVER_ENTER_DELAY_MS` opnieuw.
   */
  unfreezeSnapshot(): void {
    if (this._frozenItems().length === 0 && this._state() == null) return;
    this.writeFrozenItems([]);
    this.writeState(null);
  }

  /**
   * Verwijdert een specifieke target uit de bevroren set. Algoritme:
   *  - target.kind === 'gate':
   *      - drop gate-items met dezelfde gate;
   *      - filter gates uit gates-items, drop wanneer leeg;
   *      - laat center-items met rust (asymmetrie — een bevroren center
   *        boven de poort blijft staan).
   *  - target.kind === 'center':
   *      - drop center-items met hetzelfde center;
   *      - drop gate-items met een gate uit dat center;
   *      - filter gates uit gates-items op gates uit dat center, drop leeg.
   *
   * No-op wanneer er niets matched. `frozen()` valt automatisch terug op
   * `false` zodra de array leeg is (computed-afgeleide).
   */
  removeFromFreeze(target: HdChartHoverTarget): void {
    const items = this._frozenItems();
    if (items.length === 0) return;
    const next: HdChartHoverItem[] = [];
    if (target.kind === 'gate') {
      const gate = target.gate;
      for (const item of items) {
        if (item.kind === 'gate') {
          if (item.gate !== gate) next.push(item);
        } else if (item.kind === 'gates') {
          if (!item.gates.has(gate)) {
            next.push(item);
            continue;
          }
          const filtered = new Set(item.gates);
          filtered.delete(gate);
          if (filtered.size > 0) next.push({ kind: 'gates', gates: filtered });
        } else {
          next.push(item);
        }
      }
    } else {
      const center = target.center;
      const gatesInCenter = HD_BODYGRAPH_GATES_BY_CENTER[center];
      for (const item of items) {
        if (item.kind === 'gate') {
          if (!gatesInCenter.includes(item.gate)) next.push(item);
        } else if (item.kind === 'gates') {
          const filtered = new Set<number>();
          item.gates.forEach(g => { if (!gatesInCenter.includes(g)) filtered.add(g); });
          if (filtered.size === item.gates.size) {
            next.push(item);
          } else if (filtered.size > 0) {
            next.push({ kind: 'gates', gates: filtered });
          }
        } else {
          if (item.center !== center) next.push(item);
        }
      }
    }
    if (next.length === items.length && next.every((item, i) => item === items[i])) return;
    this.writeFrozenItems(next);
  }

  /**
   * Toggle-shortcut voor Ctrl+klik (Cmd+klik op macOS). Voegt toe wanneer de
   * target nog niet als eigen item bevroren is, verwijdert anders.
   * Pre-condition: service `enabled()` en de live-state staat op de target
   * (anders zou `addCurrentHoverToFreeze` niets te kopiëren hebben).
   */
  toggleFreezeForTarget(target: HdChartHoverTarget): void {
    if (!this._enabled()) return;
    if (this.isTargetFullyFrozen(target)) {
      this.removeFromFreeze(target);
    } else {
      this.addCurrentHoverToFreeze();
    }
  }

  /**
   * Predicaat: is de target al als eigen item bevroren? Gebruikt door het
   * page-menu om "Bevriezen" te verbergen wanneer toevoegen een no-op zou
   * zijn. Definitie:
   *  - target = gate → gate is aanwezig als gate-item óf via een gates-item.
   *    Een al-bevroren center boven de gate telt **niet** als "fully frozen"
   *    voor de gate-target (cross-kind tolerantie, §6.4).
   *  - target = center → exact dat center is als center-item bevroren.
   */
  isTargetFullyFrozen(target: HdChartHoverTarget): boolean {
    const items = this._frozenItems();
    if (target.kind === 'gate') {
      const gate = target.gate;
      for (const item of items) {
        if (item.kind === 'gate' && item.gate === gate) return true;
        if (item.kind === 'gates' && item.gates.has(gate)) return true;
      }
      return false;
    }
    const center = target.center;
    for (const item of items) {
      if (item.kind === 'center' && item.center === center) return true;
    }
    return false;
  }

  /**
   * Predicaat: bestaat er iets in de bevroren set dat door "Ontdooien
   * Selectie" op deze target zou worden geraakt? Voor centers: het center
   * zelf óf elke gate/gates-item met minstens één gate uit dit center.
   * Voor gates: een gate/gates-item dat deze gate bevat.
   */
  hasFrozenScopeFor(target: HdChartHoverTarget): boolean {
    const items = this._frozenItems();
    if (items.length === 0) return false;
    if (target.kind === 'gate') {
      const gate = target.gate;
      for (const item of items) {
        if (item.kind === 'gate' && item.gate === gate) return true;
        if (item.kind === 'gates' && item.gates.has(gate)) return true;
      }
      return false;
    }
    const center = target.center;
    const gatesInCenter = HD_BODYGRAPH_GATES_BY_CENTER[center];
    for (const item of items) {
      if (item.kind === 'center' && item.center === center) return true;
      if (item.kind === 'gate' && gatesInCenter.includes(item.gate)) return true;
      if (item.kind === 'gates') {
        for (const g of item.gates) {
          if (gatesInCenter.includes(g)) return true;
        }
      }
    }
    return false;
  }

  /**
   * Container-level safety net: wordt aangeroepen vanuit een wrapper-component
   * (chart svg-host, planet-grid host) wanneer de cursor het hele blok verlaat.
   * Doet een gedebouncede clear (zelfde `HD_HOVER_LEAVE_DELAY_MS` als de
   * gewone leave-debounce). Dekt het scenario waarin de browser het inner
   * `mouseleave`-event van het hover-target dropt bij een snelle
   * cursorbeweging naar een niet-hoverbaar gebied buiten het component.
   *
   * De `sources`-parameter is bewust enkel informatief (call-site documentatie
   * van welke container deze safety-net dekt); de geplande clear wist élke
   * actieve `_state`, ongeacht zijn `source`. Reden: een container-mouseleave
   * is een sterk signaal dat er nergens meer in de UI iets gehoverd wordt, en
   * een container-gefilterde clear faalt bij cross-container stale state
   * (bv. state staat nog op planet-grid omdat zijn inner mouseleave werd
   * gedropt — chart-host mouseleave moet die alsnog kunnen opruimen). Een
   * binnen het venster volgende `setX`-call cancelt deze timer via
   * {@link cancelPending}, zodat A→B transities tussen containers géén
   * tussenliggende clear produceren.
   *
   * **Belangrijk:** introduceer hier géén source-/container-filter — zie de
   * uitgebreide regressie-context op {@link scheduleClear}.
   *
   * Frozen items blijven onaangetast: alleen de live `_state` kan door dit
   * safety-net gewist worden.
   */
  clearForContainer(_sources: readonly HdChartHoverSource[]): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    this.cancelPending();
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (!this._enabled()) return;
      if (this._state() == null) return;
      this.writeState(null);
    }, HD_HOVER_LEAVE_DELAY_MS);
  }

  /**
   * Idempotente variant op {@link clearForContainer} bedoeld als gedelegeerde
   * `mouseover`-safety-net binnen een container (chart svg-host, planet-grid
   * host). Triggert wanneer de cursor over een non-hover-target element in de
   * container beweegt — het symptoom van het scenario waarin de browser het
   * inner `mouseleave` van een gate/center/planet-block dropt bij snelle
   * cursorbeweging. `mouseover` bubbelt (in tegenstelling tot `mouseleave`)
   * en vuurt dus altijd betrouwbaar.
   *
   * In tegenstelling tot {@link clearForContainer} **reset** deze methode
   * een lopende `pendingTimer` niet: anders zou continue cursor-beweging in
   * de dode zone tussen hover-targets een eindeloos uitgestelde timer
   * creëren, en zou een actieve enter-debounce gestoord worden door een
   * mouseover op b.v. de SVG-achtergrond. De semantiek is: "als er nog
   * niets gepland staat en er staat een actieve state, plan een gewone
   * leave-clear (72ms) die élke state opruimt".
   *
   * De `sources`-parameter is — net als bij {@link clearForContainer} —
   * informatief: de geplande clear is unconditional zodat een stale state
   * uit een andere container óók opgeruimd kan worden wanneer al haar
   * mouseleaves gedropt werden. Zie {@link scheduleClear} voor de
   * regressie-context waarom hier géén source-/container-filter staat.
   */
  softClearForContainer(_sources: readonly HdChartHoverSource[]): void {
    if (!this._enabled()) return;
    if (this._suspended()) return;
    if (this.pendingTimer != null) return;
    if (this._state() == null) return;
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (!this._enabled()) return;
      if (this._state() == null) return;
      this.writeState(null);
    }, HD_HOVER_LEAVE_DELAY_MS);
  }

  /**
   * Ultieme safety net: wanneer de cursor de hele document-viewport verlaat
   * (bv. naar de browser-chrome of buiten het venster), wissen we de
   * live hover-state direct. Geen debounce hier: er is geen "A→B"-scenario
   * mogelijk wanneer de cursor het document verlaten heeft. Tijdens freeze
   * inactief — een bevroren snapshot mag niet door cursor-uit-venster
   * verloren gaan.
   *
   * Implementatie via `mouseout` + `relatedTarget == null` op de `<html>`-root.
   * Een capture-phase `mouseleave` op `document` is **fout**: in de
   * capture-phase wordt mouseleave wél naar elke descendant gepropageerd
   * (capture loopt door alle ancestors, ook voor non-bubbling events),
   * waardoor élke mouseleave binnen de chart de state cleart en de hele
   * leave-debounce, enter-debounce en freeze-flow stuk gaat.
   */
  private installDocumentMouseLeaveSafetyNet(): void {
    const onDocumentMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget != null) return;
      if (!this._enabled()) return;
      if (this.frozen()) return;
      if (this._suspended()) return;
      this.cancelPending();
      this.writeState(null);
    };
    this.document.documentElement.addEventListener('mouseout', onDocumentMouseOut);
    this.destroyRef.onDestroy(() => {
      this.document.documentElement.removeEventListener('mouseout', onDocumentMouseOut);
    });
  }

  /**
   * Capture-phase Esc-luisteraar die uitsluitend ingrijpt wanneer er een
   * freeze actief is. We doen `stopImmediatePropagation` + `preventDefault`
   * zodat de huidige Esc-druk **niet** doorgegeven wordt aan eventueel open
   * gate-info-dialogs (die ook op Esc luisteren). Resultaat: eerste Esc heft
   * de freeze (alle items + live) op, opvolgende Esc-toetsdrukken landen op
   * de gewone Esc-bindings (bv. dialog-close). Wanneer er geen freeze actief
   * is: volledige no-op, geen interferentie met andere Esc-gedragingen.
   */
  private installEscUnfreezeListener(): void {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Een actieve annotatie-tool krijgt voorrang op de hover-ontdooi: laat Esc
      // doorlopen naar de annotatielaag (die de tool uitzet) i.p.v. te ontdooien.
      if (this.annotationStore?.active()) return;
      if (!this.frozen()) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.unfreezeSnapshot();
    };
    this.document.addEventListener('keydown', onKeyDown, true);
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('keydown', onKeyDown, true);
    });
  }

  /**
   * Bouwt het toe te voegen item uit de live state, of `null` als de live
   * state volledig dupliceert met wat al bevroren staat (zelfde kind dedupe,
   * cross-kind tolerantie — zie §6.2 / §6.4).
   */
  private buildFrozenItem(
    live: HdChartHoverState,
    existing: readonly HdChartHoverItem[],
  ): HdChartHoverItem | null {
    if (live.kind === 'gate') {
      if (this.isGateAlreadyFrozenAsGateItem(live.gate, existing)) return null;
      return { kind: 'gate', gate: live.gate };
    }
    if (live.kind === 'gates') {
      const remaining = new Set<number>();
      live.gates.forEach(g => {
        if (!this.isGateAlreadyFrozenAsGateItem(g, existing)) remaining.add(g);
      });
      if (remaining.size === 0) return null;
      return { kind: 'gates', gates: remaining };
    }
    for (const item of existing) {
      if (item.kind === 'center' && item.center === live.center) return null;
    }
    return { kind: 'center', center: live.center };
  }

  private isGateAlreadyFrozenAsGateItem(gate: number, items: readonly HdChartHoverItem[]): boolean {
    for (const item of items) {
      if (item.kind === 'gate' && item.gate === gate) return true;
      if (item.kind === 'gates' && item.gates.has(gate)) return true;
    }
    return false;
  }

  /**
   * De timer draait bewust **binnen** de Angular zone. De signal-write op
   * `_state` triggert zowel het `effect()` in de bodygraph-chart (microtask,
   * DOM-classList) als de signal-template-bindings in de planet-grid
   * componenten (`[class.is-hover-*]`, `[style.opacity]`, …). Wanneer de
   * write buiten de zone zou gebeuren, plant de `ChangeDetectionScheduler`
   * de CD-tick via een ander pad (typisch `requestAnimationFrame`) terwijl
   * het effect al in de huidige microtask uitvoert — dat veroorzaakt een
   * zichtbare 2-staps-render (chart eerst, planet-grid één frame later).
   * Door binnen de zone te blijven zitten chart-effect én planet-grid-CD in
   * dezelfde tick: één vloeiende visuele transitie. De "optimalisatie" om
   * de timer-tick buiten de zone te plannen levert verwaarloosbare winst op
   * (één CD per debounced hover-enter) en is de UX-kost niet waard.
   */
  private scheduleSet(nextState: HdChartHoverState): void {
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (!this._enabled()) return;
      this.writeState(nextState);
    }, HD_HOVER_ENTER_DELAY_MS);
  }

  /**
   * Plant een onvoorwaardelijke clear. Een binnen het venster volgende
   * `setGateHover`/`setGatesHover`/`setCenterHover` cancelt deze timer via
   * {@link cancelPending}, waardoor een A→B muisbeweging géén tussenliggende
   * `state=null` produceert. Zie de doc van {@link HD_HOVER_LEAVE_DELAY_MS}.
   *
   * **Belangrijk:** voeg hier **geen** source- of container-filter toe op de
   * `_state` die gewist wordt — ook al lijkt het intuïtief om enkel
   * "eigen-source-state" op te ruimen. Dat is een terugkerende regressie
   * waar meerdere fix-pogingen op vastliepen. De `source`-parameter is
   * bewust informatief (call-site-documentatie van wélke leave triggerde);
   * de wipe zelf moet élke actieve `_state` opruimen.
   *
   * Reden: omdat `cancelPending` aan het begin van elke nieuwe `setX` de
   * vorige leave-timer al gedood heeft, is het altijd de **laatste** leave
   * die effectief mag opruimen — ongeacht of de cursor onderweg kort over
   * een ander target gestreken heeft (zelfde container of andere). Een
   * per-source/per-container filter veroorzaakt twee leak-scenario's:
   * 1. **Same-container brush** — state = G-center (`chart-center`),
   *    brush over een naburig kanaal: `setGateHover(g, 'chart-gate')` +
   *    leave plant `scheduleClear('chart-gate')`; gefilterde clear → no-op
   *    want `chart-center` ≠ `chart-gate` → state hangt.
   * 2. **Cross-container brush** — state = G op planet-grid
   *    (`planet-grid-block`), cursor beweegt naar lege chart-area en
   *    strijkt onderweg over een channel/gate: `setGateHover(g2,
   *    'chart-gate')` doodt de planet-grids `scheduleClear` via
   *    `cancelPending` en plant zijn eigen chart-clear; gefilterde clear
   *    → no-op want state.source hoort niet bij chart → state hangt.
   *
   * Cross-container veiligheid (een chart-leave-timer mag een net-gezette
   * planet-grid-state niet wissen wanneer de gebruiker légitiem naar
   * planet-grid hovert) blijft behouden via diezelfde `cancelPending`: de
   * legitieme nieuwe `setX` kill de oude timer voor hij vuurt.
   *
   * Gedekt door regressietest `scheduled clear is unconditional
   * (cross-container brush regression)` in de bijhorende spec.
   */
  private scheduleClear(_source: HdChartHoverSource): void {
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (!this._enabled()) return;
      if (this._state() == null) return;
      this.writeState(null);
    }, HD_HOVER_LEAVE_DELAY_MS);
  }

  private cancelPending(): void {
    if (this.pendingTimer != null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }

  private cancelPendingKeynote(): void {
    if (this.pendingKeynoteTimer != null) {
      clearTimeout(this.pendingKeynoteTimer);
      this.pendingKeynoteTimer = null;
    }
    this.pendingKeynoteSource = null;
  }

  /**
   * Centrale schrijvers voor de chart-relevante state. Elke mutatie van
   * `_state`, `_frozenItems` of `_enabled` loopt langs deze methodes zodat
   * we direct erna {@link notifyStateChange} kunnen aanroepen — de synchrone
   * listeners (de bodygraph-chart) landen zo in dezelfde call-stack als de
   * signal-write. Zie {@link stateChangeListeners} en
   * `docs/human-design/hd-chart-hover-2-step-regressie-analyse.md` §7.
   */
  private writeState(state: HdChartHoverState | null): void {
    if (state != null) this._originSide.set(HD_CHART_HOVER_SOURCE_SIDE[state.source]);
    this._state.set(state);
    this.notifyStateChange();
  }

  private writeFrozenItems(items: readonly HdChartHoverItem[]): void {
    this._frozenItems.set(items);
    // De stat-markering volgt de bevroren set: verdwijnt het item waarop ze
    // slaat (Esc, sneeuwvlok-knop, "Ontdooien Alles", removeFromFreeze van één
    // poort uit de set, setEnabled(false)), dan vervalt ook de markering.
    // `toggleStatFreeze` zet `_frozenStat` vóór deze call, zodat de eigen
    // mutatie hier altijd door de check komt.
    const stat = this._frozenStat();
    if (stat != null && !items.includes(stat.item)) this._frozenStat.set(null);
    this.notifyStateChange();
  }

  private dimsSide(side: HdChartHoverSide): boolean {
    if (!this._dimOnHover()) return false;
    return !(this._dimOneSideOnly() && this._originSide() === side);
  }

  private writeEnabled(enabled: boolean): void {
    this._enabled.set(enabled);
    this.notifyStateChange();
  }

  private notifyStateChange(): void {
    for (const listener of this.stateChangeListeners) listener();
  }
}
